/** Whose voice the orb is showing. `thinking` and `idle` hold still apart from breathing. */
export type OrbState = 'idle' | 'examiner' | 'candidate' | 'thinking';

const ATTACK = 0.5;
const RELEASE = 0.12;

const clamp01 = (n: number) => (Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0);

/**
 * One animation frame of level smoothing: a louder voice is followed quickly
 * and a quieter one fades slowly, which is what makes the orb feel like it is
 * breathing with the voice instead of flickering.
 */
export function smoothLevel(previous: number, target: number, attack = ATTACK, release = RELEASE): number {
  const from = clamp01(previous);
  const to = clamp01(target);
  const rate = to > from ? attack : release;
  return clamp01(from + (to - from) * rate);
}

/**
 * A speech-like level for a voice we cannot measure (the examiner's recording
 * comes from storage without CORS headers, so the browser will not let the page
 * read its samples, and the browser's own voice has no stream at all): syllables
 * at a few hertz, rising and falling phrases, short pauses between them.
 * Repeatable for a given moment, 0-1.
 */
export function speechLevel(seconds: number): number {
  const syllable = Math.abs(Math.sin(Math.PI * 2.4 * seconds)) ** 0.8;
  const phrase = 0.55 + 0.45 * Math.sin(Math.PI * 2 * 0.37 * seconds + 1);
  const pause = Math.sin(Math.PI * 2 * 0.21 * seconds) > -0.6 ? 1 : 0.1;
  return clamp01(syllable * phrase * pause);
}

/** Where one blob of the orb sits this frame: a drift (fractions of the orb's size), a turn in degrees, and a stretch. */
export interface BlobPose {
  x: number;
  y: number;
  rotate: number;
  scaleX: number;
  scaleY: number;
}

/**
 * The blobs overlap, and each turns at its own speed and stretches on its own
 * beat, so together they read as one shape that keeps changing, like a voice
 * assistant's. `speed` is degrees a second, `orbit` how far from the centre it
 * drifts at rest, `wobble` how much it stretches at rest.
 */
const BLOBS = [
  { speed: 38, orbit: 0.045, wobble: 0.2, phase: 0 },
  { speed: -52, orbit: 0.05, wobble: 0.22, phase: 2.1 },
  { speed: 29, orbit: 0.045, wobble: 0.18, phase: 4.2 },
] as const;

export const BLOB_COUNT = BLOBS.length;

/**
 * One blob's pose at a moment. Turning does not depend on the voice, so a
 * sudden loud word cannot make the shape jump; a louder voice drifts the blobs
 * apart and stretches them instead. Repeatable for the same inputs.
 */
export function blobPose(index: number, seconds: number, voice: number): BlobPose {
  const blob = BLOBS[((index % BLOB_COUNT) + BLOB_COUNT) % BLOB_COUNT];
  const loud = clamp01(voice);
  const reach = blob.orbit * (1 + loud * 1.6);
  const drift = seconds * 0.9 + blob.phase;
  return {
    x: Math.cos(drift) * reach,
    y: Math.sin(drift) * reach,
    rotate: seconds * blob.speed,
    scaleX: 1 + blob.wobble * Math.sin(seconds * 2.1 + blob.phase) + loud * 0.24,
    scaleY: 1 + blob.wobble * Math.cos(seconds * 1.6 + blob.phase) - loud * 0.1,
  };
}

/**
 * The level the orb should show right now. `measured` is the real voice level
 * when the page has one (the candidate's microphone); the examiner falls back
 * to {@link speechLevel}.
 */
export function orbLevelFor(state: OrbState, measured: number | undefined, seconds: number): number {
  if (state === 'candidate') return clamp01(measured ?? 0);
  if (state === 'examiner') return measured === undefined ? speechLevel(seconds) : clamp01(measured);
  return 0;
}
